import type { ReactNode } from 'react';

import { Button } from '@/_shared/components/ui/button';
import { ComboboxField } from '@/_shared/components/ui/combobox';
import { InputGroup } from '@/_shared/components/ui/input-group';
import { Popover, PopoverContent, PopoverTrigger } from '@/_shared/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/_shared/components/ui/tooltip';
import { cn } from '@/_shared/lib/utils';

import type { ClauseSummary } from './lib/summarize';
import { ShapeValue } from './ShapeValue';
import type { AttributeType, ConditionValue, OperatorDef, RowBounds, ValueShape } from './types';

/** A predicate's editable clause: a **secondary button** reading the condition in plain English — the operator
 *  muted (grey) and the value in the foreground ("…" when unset) — which opens a popover holding the actual
 *  controls (`children` — a {@link ClauseEditor}). The target subject stays plain text beside it, so the row
 *  reads like a sentence.
 *
 *  A nested value combobox portals its popup OUTSIDE this popover, so its clicks would otherwise read as an
 *  outside-dismiss — `onInteractOutside` keeps the clause open while that popup is in use. (The date control's
 *  calendar is a nested Radix popover, which Radix's layer stack already handles.) */
export function PredicateClause({ summary, children }: { summary: ClauseSummary; children: ReactNode }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="secondary" size="md">
          <span className="text-muted-foreground">{summary.operator}</span>
          {summary.value != null && ` ${summary.value}`}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-max max-w-[90vw] p-2"
        onInteractOutside={(event) => {
          if ((event.target as Element | null)?.closest('[data-slot="combobox-content"]')) event.preventDefault();
        }}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

/** A fixed target rendered as a non-interactive segment styled to match the clause buttons, so a row's plain
 *  text and its clause buttons join into one segmented box via {@link ButtonGroup}. An optional `tooltip` is
 *  revealed on hover — the trigger is the segment's own `span` (asChild), not an extra wrapper, so it stays the
 *  ButtonGroup's direct child and keeps the segmented-box border/rounding. Since the tooltip is hover-only,
 *  `ariaLabel` carries the same information to assistive tech (it must include the visible text, as an
 *  aria-label replaces the accessible name). */
export function ClauseText({
  children,
  tooltip,
  ariaLabel,
}: {
  children: ReactNode;
  tooltip?: ReactNode;
  ariaLabel?: string;
}) {
  const content = (
    <span
      aria-label={ariaLabel}
      className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-grey-100 bg-background px-2 text-sm font-medium whitespace-nowrap text-grey-800"
    >
      {children}
    </span>
  );
  if (!tooltip) return content;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

/** The controls a clause popover holds — a **separate** operator select and (when the operator takes one) a
 *  **separate** value input, stacked as their own bordered fields (not fused into one InputGroup). */
export function ClauseEditor({
  operators,
  operator,
  onOperatorChange,
  operatorAriaLabel,
  shape,
  value,
  onValueChange,
  valueAriaBase,
  type,
  bounds,
}: {
  operators: readonly OperatorDef[];
  operator: string;
  onOperatorChange: (value: string) => void;
  operatorAriaLabel: string;
  shape: ValueShape;
  value: ConditionValue;
  onValueChange: (next: ConditionValue) => void;
  valueAriaBase: string;
  /** Column type — forwarded to {@link ShapeValue} so numeric membership keeps `number[]`. */
  type?: AttributeType;
  /** The column's observed min/max — forwarded to {@link ShapeValue}, rendered by the date controls only. */
  bounds?: RowBounds;
}) {
  return (
    // items-start so the operator select stays top-aligned when a wrapping pill list grows the value box taller.
    <div className="flex items-start gap-2">
      <ComboboxField
        variant="popup"
        size="md"
        options={operators.map((o) => ({ value: o.value, label: o.label }))}
        value={operator}
        onValueChange={onOperatorChange}
        aria-label={operatorAriaLabel}
      />
      {shape !== 'none' && (
        // Only the enum/pill shapes wrap taller than the field, so give THEM h-auto (with max-w-md capping the
        // wrap). Every single-line control (number, date, …) keeps the md height so the value box lines up with
        // the operator select beside it — `items-start` above then aligns two equal-height boxes.
        <InputGroup
          size="md"
          className={cn(shape === 'single_enum' || shape === 'multi_enum' ? 'h-auto' : '', 'w-max max-w-md')}
          aria-label={`${valueAriaBase} value`}
        >
          <ShapeValue
            shape={shape}
            value={value}
            onPatch={onValueChange}
            ariaBase={valueAriaBase}
            type={type}
            bounds={bounds}
          />
        </InputGroup>
      )}
    </div>
  );
}
